import { useEffect, useState } from "react";
import { Building2, FileText, Store } from "lucide-react";
import { api, fmtDate, fmtTL } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SuppliersTab from "../components/admin/SuppliersTab";
import StoresTab from "../components/admin/StoresTab";

function InvoicesTab() {
    const [invoices, setInvoices] = useState(null);
    const [invQ, setInvQ] = useState("");
    const [invStatus, setInvStatus] = useState("all");
    const [invSort, setInvSort] = useState("date");
    useEffect(() => { api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([])); }, []);

    if (!invoices) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;

    const shown = invoices
        .filter((i) => !invQ || (i.invoice_no || "").toLowerCase().includes(invQ.toLowerCase()) || (i.supplier_name || "").toLowerCase().includes(invQ.toLowerCase()) || (i.buyer_company_name || "").toLowerCase().includes(invQ.toLowerCase()))
        .filter((i) => invStatus === "all" || i.status === invStatus)
        .sort((a, b) =>
            invSort === "amount" ? (b.total || 0) - (a.total || 0)
            : invSort === "due" ? (a.payment_days_left ?? 9999) - (b.payment_days_left ?? 9999)
            : new Date(b.issued_at || 0) - new Date(a.issued_at || 0)
        );

    return (
        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="cari-invoices-table">
            <div className="flex flex-wrap items-center gap-2 border-b p-3" data-testid="cari-invoices-filters">
                <input value={invQ} onChange={(e) => setInvQ(e.target.value)} placeholder="Fatura no, tedarikçi, mağaza ara..." className="h-8 w-64 rounded-md border bg-background px-3 text-xs" data-testid="cari-invoice-search" />
                <select value={invStatus} onChange={(e) => setInvStatus(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs" data-testid="cari-invoice-status">
                    <option value="all">Durum: Tümü</option>
                    <option value="issued">Kesildi</option>
                    <option value="paid">Ödendi</option>
                    <option value="returned">İade</option>
                    <option value="cancelled">İptal</option>
                </select>
                <select value={invSort} onChange={(e) => setInvSort(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs" data-testid="cari-invoice-sort">
                    <option value="date">Tarih (yeniden eskiye)</option>
                    <option value="amount">Tutar (çoktan aza)</option>
                    <option value="due">Vade (yaklaşan önce)</option>
                </select>
                <span className="ml-auto text-xs text-muted-foreground">{shown.length} fatura</span>
            </div>
            <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                        <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <th className="p-3">Fatura</th><th className="p-3">Taraflar</th><th className="p-3">Tutar</th><th className="p-3">PDF</th><th className="p-3">7 Gün Süre</th><th className="p-3">Vade</th><th className="p-3">Ödeme</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((inv) => (
                            <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`cari-invoice-${inv.invoice_no}`}>
                                <td className="p-3 font-mono font-semibold">{inv.invoice_no}</td>
                                <td className="p-3 text-xs text-muted-foreground">{inv.supplier_name} → {inv.order_no}</td>
                                <td className="p-3 font-display font-bold text-terra-600">{fmtTL(inv.total)}</td>
                                <td className="p-3">{inv.pdf_uploaded ? <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Yüklendi</Badge> : <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800">Bekliyor</Badge>}</td>
                                <td className="p-3 text-xs">
                                    {inv.pdf_uploaded ? "—" : inv.days_left === null || inv.days_left === undefined ? "—" : inv.days_left < 0 ? <span className="font-semibold text-red-600">Süre doldu</span> : <span className={inv.days_left <= 3 ? "font-semibold text-amber-700" : "text-muted-foreground"}>{inv.days_left} gün kaldı</span>}
                                </td>
                                <td className="p-3 text-xs" data-testid={`cari-vade-${inv.invoice_no}`}>
                                    {inv.status !== "issued" ? "—" : inv.payment_days_left === null || inv.payment_days_left === undefined ? "—" : inv.payment_overdue ? <span className="font-semibold text-red-600">{-inv.payment_days_left} gün gecikti</span> : <span className={inv.payment_days_left <= 3 ? "font-semibold text-amber-700" : "text-muted-foreground"}>{fmtDate(inv.payment_due_at)} ({inv.payment_days_left} gün)</span>}
                                </td>
                                <td className="p-3 text-xs" data-testid={`cari-payment-${inv.invoice_no}`}>{inv.payment_state || (inv.status === "paid" ? "Ödeme Tamamlandı" : "Ödeme Bekliyor")}</td>
                            </tr>
                        ))}
                        {!invoices.length && <tr><td colSpan={6} className="p-16 text-center text-sm text-muted-foreground"><FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Fatura yok.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function CariHesaplar() {
    return (
        <div className="space-y-6" data-testid="cari-hesaplar-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Cari Hesaplar</h1>
                <p className="mt-1 text-sm text-muted-foreground">Tedarikçiler, mağazalar ve fatura takibi tek çatı altında.</p>
            </div>
            <Tabs defaultValue="suppliers" data-testid="cari-tabs">
                <TabsList>
                    <TabsTrigger value="suppliers" data-testid="cari-tab-suppliers"><Store className="mr-2 h-4 w-4" /> Tedarikçiler</TabsTrigger>
                    <TabsTrigger value="stores" data-testid="cari-tab-stores"><Building2 className="mr-2 h-4 w-4" /> Mağazalar</TabsTrigger>
                    <TabsTrigger value="invoices" data-testid="cari-tab-invoices"><FileText className="mr-2 h-4 w-4" /> Faturalar</TabsTrigger>
                </TabsList>
                <TabsContent value="suppliers" className="mt-4"><SuppliersTab /></TabsContent>
                <TabsContent value="stores" className="mt-4"><StoresTab /></TabsContent>
                <TabsContent value="invoices" className="mt-4"><InvoicesTab /></TabsContent>
            </Tabs>
        </div>
    );
}

export function fmtDateShort(iso) { return fmtDate(iso); }
